import type {
  ActivityCockpitSnapshot,
  CockpitGuaranteeResult,
} from '@internal/spassu-code-contract';
import type {
  CockpitControlView,
  CockpitGuaranteeView,
} from './cockpitViewModel';
import {
  controlResultLabel,
  guaranteeStatusLabel,
  limitationLabel,
} from './semanticLabels';

export function createCockpitGuaranteeView(
  snapshot: ActivityCockpitSnapshot,
): CockpitGuaranteeView {
  const summary = snapshot.sections.summary.value;
  const section = snapshot.sections.guarantees;
  const status = section.value?.status ?? summary?.guarantee ?? 'unavailable';
  return {
    status,
    statusLabel: guaranteeStatusLabel(status),
    sectionStatus: section.status,
    coverage: section.coverage,
    reason: guaranteeReason(status, section.value?.gapControlIds.length ?? 0),
    impact: guaranteeImpact(status),
    controls: (section.value?.controls ?? []).map(control =>
      controlView(control, section.coverage),
    ),
    criticalGapIds: summary?.criticalGapIds ?? [],
    limitations: uniqueLimitations(snapshot).map(code => ({
      code,
      summary: limitationLabel(code),
    })),
  };
}

function controlView(
  control: NonNullable<
    ActivityCockpitSnapshot['sections']['guarantees']['value']
  >['controls'][number],
  coverage: CockpitControlView['coverage'],
): CockpitControlView {
  const critical =
    control.blocking &&
    control.result !== 'passed' &&
    control.result !== 'not-applicable';
  return {
    id: control.id,
    kind: 'control',
    label: control.label,
    confidence: controlConfidence(control.result),
    lifecycle: controlLifecycle(control.result),
    coverage,
    critical,
    result: control.result,
    resultLabel: controlResultLabel(control.result),
    sourceLabel:
      control.source === 'baseline' ? 'Controle padrão' : 'Política do projeto',
    required: control.required,
    blocking: control.blocking,
    evidenceIds: control.evidenceIds,
    observedTotal: control.observedTotal,
    failedTotal: control.failedTotal,
    rationale: control.rationale,
    expectedAction: control.expectedAction,
  };
}

function controlConfidence(result: CockpitGuaranteeResult) {
  if (result === 'passed' || result === 'failed') return 'confirmed' as const;
  if (result === 'partial') return 'partial' as const;
  if (result === 'not-applicable') return 'confirmed' as const;
  return 'unavailable' as const;
}

function controlLifecycle(result: CockpitGuaranteeResult) {
  if (result === 'passed' || result === 'not-applicable')
    return 'completed' as const;
  if (result === 'missing' || result === 'unknown') return 'unknown' as const;
  return 'interrupted' as const;
}

function guaranteeReason(
  status: CockpitGuaranteeView['status'],
  gapCount: number,
): string {
  if (status === 'complete')
    return 'Todos os controles obrigatórios observados foram atendidos.';
  if (status === 'incomplete') {
    if (gapCount === 0) return 'Há controles com lacuna ou resultado parcial.';
    if (gapCount === 1)
      return 'Existe 1 controle com lacuna ou resultado parcial.';
    return `Existem ${gapCount} controles com lacuna ou resultado parcial.`;
  }
  return 'Não há evidência suficiente para determinar a garantia.';
}

function guaranteeImpact(status: CockpitGuaranteeView['status']): string {
  if (status === 'complete')
    return 'A atividade possui evidências suficientes para a leitura atual.';
  if (status === 'incomplete')
    return 'A interpretação deve considerar as lacunas e ações indicadas.';
  return 'Não é possível confirmar a confiabilidade da atividade neste retrato.';
}

function uniqueLimitations(snapshot: ActivityCockpitSnapshot): string[] {
  return [
    ...new Set(
      Object.values(snapshot.sections).flatMap(section => section.limitations),
    ),
  ];
}
